'use client';

import { useState } from 'react';

interface NewsletterSignupProps {
  className?: string;
  source?: string;
}

export default function NewsletterSignup({ className = '', source = 'newsletter-widget' }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await fetch('/api/newsletter/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          source,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        setMessage(data.message);
        setEmail('');
        
        // Reset success state after 5 seconds
        setTimeout(() => {
          setIsSuccess(false);
          setMessage('');
        }, 5000);
      } else {
        setIsSuccess(false);
        setMessage(data.message);
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      setIsSuccess(false);
      setMessage('Failed to subscribe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg p-6 text-white ${className}`}>
      <h3 className="font-bold text-lg mb-3">Stay Updated</h3>
      <p className="text-blue-100 text-sm mb-4">
        Get the latest crypto insights delivered to your inbox.
      </p>
      
      {isSuccess ? (
        <div className="text-center py-4">
          <div className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <span className="text-white text-xl">✓</span>
          </div>
          <p className="text-sm font-medium">{message}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full px-3 py-2 rounded text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            required
            disabled={isLoading}
          />
          <button 
            type="submit"
            disabled={isLoading || !email.trim()}
            className="w-full bg-white text-blue-600 py-2 rounded font-semibold text-sm hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Subscribing...' : 'Subscribe'}
          </button>
          
          {message && !isSuccess && (
            <p className="text-red-200 text-xs mt-2 text-center">
              {message}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

